import { Collapse } from '@vrplatform/components';
import { useApiQuery } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { LazyTable, type MRT_ColumnDef } from '@vrplatform/table';
import {
  type Maybe,
  ensure,
  formatCurrency,
  groupBy,
  sum,
  toTitleCase,
} from '@vrplatform/utils';
import {
  Anchor,
  Box,
  Center,
  Group,
  HoverCard,
  List,
  LoadingOverlay,
  Tooltip,
  rem,
} from '@mantine/core';
import { Text } from '@mantine/core';
import { formatAccountType } from '@vrplatform/ui-common';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { getReservationFinancialGroup } from '../../../../../drawers/reservation-drawers/_utils';
import { FEE_TEMPLATES } from '../../../overview/_constants';
import { type FeeFormInputs, useFeeForm } from '../../useFeeForm';
import { getFlatFeeFormula } from '../../useFeeForm.utils';
import {
  buildFeeAccountTotalConditions,
  shouldRequireFeeLinkedAccounts,
} from '../FeeAmountFilterEditor.utils';
import { useFeeAccountsTableFilter } from '../FeeAccountsTable';
import { getFeeReservationPreviewWithAppliedFilters } from './FeeReservationPreviewTable.utils';
import type { FeeReservationPreview } from './useFeeReservationPreviewQuery';

export type PreviewRow = {
  id: string;
  type: 'financial' | 'fee' | 'deposit' | 'expense';
  title: string;
  centTotal: number;
  accounts: { id: string; centTotal: number }[];
  subRows?: PreviewRow[];
};

export const STORYBOOK_FEE_RESERVATION_PREVIEW_QUERY_KEY =
  'fee-reservation-preview-table';

export function getFeeFormula(data: {
  rateType: FeeFormInputs['rateType'] | undefined;
  rate: FeeFormInputs['rate'];
  type?: FeeFormInputs['type'];
  flatFeeBasis: FeeFormInputs['flatFeeBasis'] | undefined;
  linkedAccountIds: FeeFormInputs['linkedAccountIds'] | undefined;
}) {
  const linkedAccounts = Array.isArray(data.linkedAccountIds)
    ? data.linkedAccountIds
    : [];
  const accountExpression =
    linkedAccounts.length > 0
      ? linkedAccounts.map((x) => `"${x}"`).join(' + ')
      : '0';

  if (data.rateType === 'flat') {
    return getFlatFeeFormula(data.flatFeeBasis);
  }

  const template =
    data.type && FEE_TEMPLATES[data.type]
      ? FEE_TEMPLATES[data.type].config.formulaTemplate
      : FEE_TEMPLATES.additionalFee.config.formulaTemplate;

  return template.replace('accounts', accountExpression);
}

function usePreviewQuery(reservationId: Maybe<string>) {
  const methods = useFeeForm();

  const recurringFeeId = useWatch({
    control: methods.control,
    name: 'id',
  });

  const linkedAccountIds = useWatch({
    control: methods.control,
    name: 'linkedAccountIds',
  });
  const name = useWatch({
    control: methods.control,
    name: 'title',
  })?.trim();
  const type = useWatch({
    control: methods.control,
    name: 'type',
  });

  const defaultRate = useWatch({
    control: methods.control,
    name: 'rate',
  });

  const rateType = useWatch({
    control: methods.control,
    name: 'rateType',
  });

  const flatFeeBasis = useWatch({
    control: methods.control,
    name: 'flatFeeBasis',
  });

  const bookingChannelsFilter = useWatch({
    control: methods.control,
    name: 'bookingChannelsFilter',
  });

  const creditAccountId = useWatch({
    control: methods.control,
    name: 'creditAccountId',
  });

  const debitAccountId = useWatch({
    control: methods.control,
    name: 'debitAccountId',
  });

  const revenueRecognition = useWatch({
    control: methods.control,
    name: 'revenueRecognition',
  });

  const statusFilter = useWatch({
    control: methods.control,
    name: 'statusFilter',
  });
  const amountFilter = useWatch({
    control: methods.control,
    name: 'accountTotalConditions',
  });

  const taxRateId = useWatch({
    control: methods.control,
    name: 'taxRateId',
  });

  const rateIsValid =
    typeof defaultRate === 'number' &&
    defaultRate >= 0 &&
    defaultRate <= 100000;

  const hasTemplate = Boolean(type && FEE_TEMPLATES[type]);
  const canPreview = Boolean(reservationId) && rateIsValid && hasTemplate;
  const accountTotalConditions = useMemo(
    () =>
      buildFeeAccountTotalConditions({
        amountFilter,
        linkedAccountIds,
      }),
    [amountFilter, linkedAccountIds]
  );

  return useApiQuery(
    async (api, signal, args) => {
      if (!args.reservationId) return null;

      const res = await api.POST('/recurring-fees/preview', {
        body: {
          reservationId: args.reservationId,
          type: args.type,
          defaultRate: args.defaultRate,
          formula: getFeeFormula({
            linkedAccountIds: args.linkedAccountIds,
            rate: args.defaultRate,
            rateType: args.rateType,
            type: args.type,
            flatFeeBasis: args.flatFeeBasis,
          }),
          rateType: args.rateType,
          creditAccountId: args.creditAccountId,
          debitAccountId: args.debitAccountId,
          bookingChannelsFilter:
            args.bookingChannelsFilter === 'all-channels'
              ? null
              : args.bookingChannelsFilter,
          revenueRecognition: args.revenueRecognition,
          statusFilter: args.statusFilter,
          accountTotalConditions: args.accountTotalConditions,
          taxRateId: args.taxRateId,
          id: args.recurringFeeId ?? undefined,
          name: args.name,
        },
        signal,
      });

      if (res.error) {
        throw res.error;
      }

      return res.data;
    },
    {
      queryGroup: 'fees',
      uniqueQueryKey: 'fee-reservation-preview',
      storybookQueryKey: STORYBOOK_FEE_RESERVATION_PREVIEW_QUERY_KEY,
      variables: {
        reservationId,
        linkedAccountIds,
        name,
        defaultRate,
        rateType,
        type,
        bookingChannelsFilter,
        creditAccountId,
        debitAccountId,
        revenueRecognition,
        statusFilter,
        accountTotalConditions,
        taxRateId,
        flatFeeBasis,
        recurringFeeId,
      },
      keepPreviousData: true,
      skip: !canPreview,
    }
  );
}

type LineGroup = ReturnType<typeof getReservationFinancialGroup> | '-';
type PreviewLine = NonNullable<
  ReturnType<typeof usePreviewQuery>['data']
>['lines'][number] & {
  subRows?: (PreviewLine & { group: LineGroup })[];
  group: LineGroup;
};

export const FeeReservationPreviewTable = ({
  reservation,
}: {
  reservation: FeeReservationPreview;
}) => {
  const { setFilter } = useFeeAccountsTableFilter();
  const methods = useFeeForm();
  const {
    data: previewResponse,
    isFetching,
    isInitialLoading,
  } = usePreviewQuery(reservation?.id);

  const type = useWatch({
    control: methods.control,
    name: 'type',
  });
  const bookingChannelsFilter = useWatch({
    control: methods.control,
    name: 'bookingChannelsFilter',
  });
  const statusFilter = useWatch({
    control: methods.control,
    name: 'statusFilter',
  });
  const amountFilter = useWatch({
    control: methods.control,
    name: 'accountTotalConditions',
  });
  const rateType = useWatch({
    control: methods.control,
    name: 'rateType',
  });
  const linkedAccountIds =
    useWatch({
      control: methods.control,
      name: 'linkedAccountIds',
    }) ?? [];
  const linkedAccountsRequired = shouldRequireFeeLinkedAccounts({
    rateType,
    amountFilter,
  });

  const currency = reservation?.currency ?? 'usd';
  const accountTotalConditions = useMemo(
    () =>
      buildFeeAccountTotalConditions({
        amountFilter,
        linkedAccountIds,
      }),
    [amountFilter, linkedAccountIds]
  );
  const preview = useMemo(() => {
    const filteredPreview = getFeeReservationPreviewWithAppliedFilters({
      preview: previewResponse,
      reservation,
      filters: {
        bookingChannelsFilter,
        statusFilter,
        accountTotalConditions,
      },
    });

    if (
      !filteredPreview ||
      !linkedAccountsRequired ||
      linkedAccountIds.length > 0
    ) {
      return filteredPreview;
    }

    return {
      ...filteredPreview,
      totals: {
        ...filteredPreview.totals,
        fee: 0,
      },
      lines: filteredPreview.lines.map((line) => ({
        ...line,
        fee: 0,
      })),
    };
  }, [
    accountTotalConditions,
    bookingChannelsFilter,
    linkedAccountIds.length,
    linkedAccountsRequired,
    previewResponse,
    reservation,
    statusFilter,
  ]);

  const lines = useMemo(() => {
    const filtered =
      preview?.lines
        .filter((x) => {
          const party = x.party ?? 'manager';

          const d =
            party === 'owners' ||
            (party === 'manager' &&
              (x.type?.includes('deposit') || x.type === 'reservation_fee'));

          if (type === 'managementFee')
            return d && x.recurringFee?.type !== 'managementFee';

          return d;
        })
        .sort((a, b) => {
          const order: Record<NonNullable<(typeof a)['type']> | '-', number> = {
            reservation: 0,
            reservation_line: 1,
            reservation_adjustment: 2,
            reservation_adjustment_cancellation: 3,
            reservation_fee: 4,
            reservation_fee_adjustment: 5,
            reservation_fee_tax: 6,
            transaction_deposit: 7,
            transaction_deposit_line: 8,
            transaction_deposit_reserve: 9,
            transaction_deposit_refund: 10,
            transaction_deposit_channelFee: 11,
            transaction_deposit_merchantFee: 12,
            transaction_deposit_coHost: 13,
            transaction_deposit_openingBalance: 14,
            transaction_deposit_openingAdvance: 15,
            transaction_deposit_vat: 16,
            transaction_expense: 17,
            transaction_expense_line: 18,
            transaction_expense_payment: 19,
            transaction_expense_markup: 20,
            transaction_expense_markup_tax: 21,
            transaction_transfer: 22,
            transaction_transfer_line: 23,
            transaction_payout: 24,
            transaction_payout_line: 25,
            '-': 1000,
          };

          return (order[a.type ?? '-'] ?? 0) - (order[b.type ?? '-'] ?? 0);
        }) ?? [];

    return Object.entries(
      groupBy(filtered, (x) => {
        const group = getReservationFinancialGroup(x);

        return group || '-';
      })
    ).map<PreviewLine>(([t, lines]) => {
      const group = t as LineGroup;

      const name = (() => {
        if (group === 'feeAndCommission') return 'Fee & Commissions';
        if (group === 'financial') return 'Financials';
        return toTitleCase(group);
      })();

      const actives = lines.filter((x) => x.status === 'active');

      return {
        amount: sum(actives, 'amount'),
        fee: sum(actives, 'fee'),
        subRows: lines.map((x) => ({ ...x, group })),
        name,
        group,
        status: lines.some((x) => x.status === 'active')
          ? 'active'
          : 'inactive',
        type: lines[0].type,
        account: null,
        lineType: null,
        recurringFee: null,
        transaction: null,
      };
    });
  }, [preview?.lines, type]);

  const columns = useMemo<MRT_ColumnDef<PreviewLine>[]>(
    () => [
      {
        header: 'Item',
        Header: '',
        mantineTableFooterCellProps: {
          pl: 'xs',
        },
        mantineTableBodyCellProps: {
          px: 'xs',
        },
        Footer: () => (
          <Text span fw={500}>
            Total charged:
          </Text>
        ),
        AggregatedCell: ({ row }) => {
          const title = row.original.name;
          return <Text fw={500}>{title}</Text>;
        },
        Cell: ({ row }) => {
          const title = row.original.name;
          const fw = !row.parentId ? 500 : undefined;
          const td =
            row.original.status === 'inactive' ? 'line-through' : undefined;

          const accounts = row.original.account ? [row.original.account] : [];

          const isAdjustment =
            row.original.type &&
            ensure<NonNullable<(typeof row)['original']['type']>[]>([
              'reservation_adjustment',
              'reservation_adjustment_cancellation',
              'reservation_fee_adjustment',
            ]).includes(row.original.type);

          return (
            <HoverCard
              shadow="md"
              withArrow
              withinPortal
              position="top"
              radius="md"
            >
              <HoverCard.Target>
                <Box>
                  <Text td={td} fw={fw}>
                    {title}
                  </Text>
                  {isAdjustment && (
                    <Text td={td} fw={400} size="xs" c="gray">
                      Adjustment
                    </Text>
                  )}
                </Box>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <Text fw={500} mb={rem(5)}>
                  Accounts
                </Text>
                {accounts.length ? (
                  <List icon={null} size="sm">
                    {accounts.map((x) => (
                      <List.Item key={x.id}>
                        <Anchor
                          component="button"
                          onClick={() => setFilter({ search: x.name })}
                          c="dark"
                        >
                          {x.name}
                        </Anchor>
                      </List.Item>
                    ))}
                  </List>
                ) : (
                  <Text c="gray">No account found</Text>
                )}
              </HoverCard.Dropdown>
            </HoverCard>
          );
        },
      },
      {
        header: 'Total',
        maxSize: 90,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableHeadCellProps: {
          align: 'right',
          pr: 'xs',
        },
        mantineTableFooterCellProps: {
          align: 'right',
          pr: 'xs',
        },
        Cell: ({ row }) => {
          const amount = formatCurrency(
            (row.original.amount || 0) / 100,
            currency
          );

          const fw = !row.parentId ? 500 : undefined;

          const td =
            row.original.status === 'inactive' ? 'line-through' : undefined;

          return (
            <Text td={td} fw={fw}>
              {amount}
            </Text>
          );
        },
        AggregatedCell: ({ row }) => {
          return (
            <Text fw={500}>
              {formatCurrency(row.original.amount / 100, currency)}
            </Text>
          );
        },
        // Footer: () => (
        //   <Text span fw={500} ta="right" display={'block'}>
        //     {formatCurrency((preview?.totals.owner ?? 0) / 100, currency)}
        //   </Text>
        // ),
      },
      {
        header: 'Fee',
        maxSize: 90,
        mantineTableBodyCellProps: {
          align: 'right',
          sx: (theme) => ({
            backgroundColor: theme.colors.yellow[0],
          }),
        },
        mantineTableFooterCellProps: {
          align: 'right',
          pr: 'xs',
          sx: (theme) => ({
            backgroundColor: theme.colors.yellow[0],
          }),
        },
        mantineTableHeadCellProps: {
          align: 'right',
          pr: 'xs',
          sx: (theme) => ({
            backgroundColor: theme.colors.yellow[0],
            fontWeight: 400,
            color: theme.colors.gray[7],
          }),
        },
        AggregatedCell: ({ row }) => {
          const fee = row.original.fee;

          if (!fee) return '-';

          const td =
            row.original.status === 'inactive' ? 'line-through' : undefined;

          return (
            <Text td={td} fw={500}>
              {formatCurrency(fee / 100, currency)}
            </Text>
          );
        },
        Cell: ({ row }) => {
          const fee = row.original.fee;
          const accountType = row.original?.account?.type ?? 'ledger';

          if (!fee) return '-';

          const amount = formatCurrency(fee / 100, currency);

          const fw = !row.parentId ? 500 : undefined;

          const td =
            row.original.status === 'inactive' && accountType !== 'nonPosting'
              ? 'line-through'
              : undefined;

          if (accountType === 'nonPosting')
            return (
              <Group>
                <Tooltip
                  multiline
                  maw={400}
                  label={`This line is mapped to a ${formatAccountType('nonPosting')} account, which results in the line amount to not be included in the reservation total. But when mapped in a fee, the line amount will get included in the fee calculation`}
                >
                  <Icon icon="InfoIcon" size={16} />
                </Tooltip>
                <Text td={td} fw={fw}>
                  {amount}
                </Text>
              </Group>
            );

          return (
            <Text td={td} fw={fw}>
              {amount}
            </Text>
          );
        },
        Footer: () => (
          <Text span fw={500} ta="right" display={'block'}>
            {formatCurrency((preview?.totals.fee ?? 0) / 100, currency)}
          </Text>
        ),
      },
    ],
    [currency, preview?.totals.fee, setFilter]
  );

  return (
    <Box pos="relative">
      <Group
        mb="md"
        justify="space-between"
        wrap="nowrap"
        sx={(theme) => ({
          padding: theme.spacing.xs,
          backgroundColor: theme.colors.yellow[0],
          borderRadius: theme.radius.md,
        })}
      >
        <Title />
        <Text fw={500} ta="right">
          {formatCurrency((preview?.totals.fee ?? 0) / 100, currency)}
        </Text>
      </Group>

      <Collapse
        title={
          <Text component="span" size="sm">
            Preview
          </Text>
        }
        rightSection={null}
        minHeight={30}
        defaultOpened
        hideToggle
      >
        <Box>
          <LoadingOverlay
            visible={isFetching && !isInitialLoading}
            zIndex={100}
          />
          <LazyTable
            table={{
              columns,
              hideHeader: true,
              hideTopBar: true,
              emptyRowsFallback: () => (
                <Center>
                  <Text size="sm" c="gray">
                    No lines found
                  </Text>
                </Center>
              ),
            }}
            subRows={{
              getRowCanExpand: (row) => !!row.original.subRows?.length,
              defaultExpanded: true,
              getSubRows: (row) => row.subRows ?? [],
            }}
            data={{
              rows: lines,
              rowCount: lines.length,
              error: null,
              loading: isInitialLoading,
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

const Title = () => {
  const methods = useFeeForm();
  const title = useWatch({
    control: methods.control,
    name: 'title',
  })?.trim();

  if (!title)
    return (
      <Text ta="left" c="neutral">
        (Missing name)
      </Text>
    );

  return (
    <Text ta="left" fw={500}>
      {title}
    </Text>
  );
};
